import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import Tabs from '@mui/material/Tabs';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { RuTertiaryButton } from '@silverrailtech/srtlibrary';
import Header from '../components/Header/Header';
import ExtendedSnackbar from '../components/ExtendedSnackbar/ExtendedSnackbar';
import SubmitVouchersAction from './components/SubmitVouchersAction/SubmitVouchersAction';
import VoucherSubmissionResultDialogue from './components/VoucherSubmissionResultDialogue/VoucherSubmissionResultDialogue';
import VouchersPagination from './components/VouchersPagination/VouchersPagination';
import VouchersTable from './components/VouchersTable/VouchersTable';
import VouchersToolbar from './components/VouchersToolbar/VouchersToolbar';
import useArchiveCsvExport from './hooks/useArchiveCsvExport';
import useArchivedVouchers from './hooks/useArchivedVouchers';
import useAvailableVouchers from './hooks/useAvailableVouchers';
import useVoucherSubmission from './hooks/useVoucherSubmission';
import messages from './messagesOuigoRefunds';
import inlineStyles, { PageTitle, TabBar, OuiGoTab } from './styles';
import {
	ISSUE_DATE_SORT_COLUMN,
	getIssueDateSortBy,
	getNextSortState,
	sortVouchers,
} from './utils';

const parseFilterDate = (dateString) =>
	dateString ? parse(dateString, 'yyyy-MM-dd', new Date()) : null;

const OuigoRefunds = ({
	exportFinalizedVouchers,
	loadEligibleVouchers,
	loadFinalizedVouchers,
	loginId,
	settings,
	submitVouchers,
}) => {
	const intl = useIntl();
	const hasSettings = !!(loginId && settings);
	const [selectedTab, setSelectedTab] = useState(0);
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	const [appliedFromDate, setAppliedFromDate] = useState(null);
	const [appliedToDate, setAppliedToDate] = useState(null);
	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [archiveFromDate, setArchiveFromDate] = useState(null);
	const [archiveToDate, setArchiveToDate] = useState(null);
	const [appliedArchiveFromDate, setAppliedArchiveFromDate] = useState(null);
	const [appliedArchiveToDate, setAppliedArchiveToDate] = useState(null);
	const [archiveSortColumn, setArchiveSortColumn] = useState(null);
	const [archiveSortDirection, setArchiveSortDirection] = useState(null);
	const [archiveCurrentPage, setArchiveCurrentPage] = useState(0);
	const availableIssueDateSortBy = getIssueDateSortBy(
		sortColumn,
		sortDirection,
	);
	const archiveIssueDateSortBy = getIssueDateSortBy(
		archiveSortColumn,
		archiveSortDirection,
	);
	const {
		filter: availableFilter,
		hasError: hasAvailableVouchersError,
		isLoading: isAvailableVouchersLoading,
		pagination: availablePagination,
		reload: reloadAvailableVouchers,
		removeVouchers: removeAvailableVouchers,
		vouchers: availableVoucherData,
	} = useAvailableVouchers({
		appliedFromDate,
		appliedToDate,
		currentPage,
		enabled: hasSettings && selectedTab === 0,
		intl,
		loadEligibleVouchers,
		sortBy: availableIssueDateSortBy,
	});
	const {
		filter: archiveFilter,
		hasError: hasArchivedVouchersError,
		isLoading: isArchivedVouchersLoading,
		pagination: archivePagination,
		vouchers: archivedVoucherData,
	} = useArchivedVouchers({
		appliedFromDate: appliedArchiveFromDate,
		appliedToDate: appliedArchiveToDate,
		currentPage: archiveCurrentPage,
		enabled: hasSettings && selectedTab === 1,
		intl,
		loadFinalizedVouchers,
		sortBy: archiveIssueDateSortBy,
	});

	useEffect(() => {
		if (availableFilter) {
			setFromDate(parseFilterDate(availableFilter.startDate));
			setToDate(parseFilterDate(availableFilter.endDate));
		}

		if (archiveFilter) {
			setArchiveFromDate(parseFilterDate(archiveFilter.startDate));
			setArchiveToDate(parseFilterDate(archiveFilter.endDate));
		}
	}, [availableFilter, archiveFilter]);

	const {
		clearError: clearArchiveCsvError,
		download: downloadArchiveCsv,
		hasError: hasArchiveCsvError,
		isDownloading: isArchiveCsvDownloading,
	} = useArchiveCsvExport({
		appliedFromDate: appliedArchiveFromDate,
		appliedToDate: appliedArchiveToDate,
		exportFinalizedVouchers,
		sortBy: archiveIssueDateSortBy,
	});
	const availableVouchers =
		sortColumn === ISSUE_DATE_SORT_COLUMN
			? availableVoucherData
			: sortVouchers(availableVoucherData, sortColumn, sortDirection);
	const {
		clearSelection: clearVoucherSelection,
		closeResult: handleSubmissionResultClose,
		handleCurrentPageSelectionChange,
		handleVoucherSelectionChange,
		isSubmitting: isSubmittingVouchers,
		messages: submissionMessages,
		result: submissionResult,
		selectedVoucherIds,
		submit: handleSubmitVouchers,
	} = useVoucherSubmission({
		currentPageVouchers: availableVouchers,
		onResultsRefresh: () => setCurrentPage(0),
		reloadAvailableVouchers,
		removeAvailableVouchers,
		submitVouchers,
	});
	const shouldShowAvailableResults =
		!isAvailableVouchersLoading && !hasAvailableVouchersError;
	const availableResultTotal = shouldShowAvailableResults
		? availablePagination.totalItems
		: 0;
	const resultStart = shouldShowAvailableResults
		? availablePagination.resultStart
		: 0;
	const resultEnd = shouldShowAvailableResults
		? availablePagination.resultEnd
		: 0;
	const archivedVouchers =
		archiveSortColumn === ISSUE_DATE_SORT_COLUMN
			? archivedVoucherData
			: sortVouchers(
					archivedVoucherData,
					archiveSortColumn,
					archiveSortDirection,
				);
	const shouldShowArchiveResults =
		!isArchivedVouchersLoading && !hasArchivedVouchersError;
	const archiveResultTotal = shouldShowArchiveResults
		? archivePagination.totalItems
		: 0;
	const archiveResultStart = shouldShowArchiveResults
		? archivePagination.resultStart
		: 0;
	const archiveResultEnd = shouldShowArchiveResults
		? archivePagination.resultEnd
		: 0;

	const handleTabChange = (_, value) => {
		setSelectedTab(value);
	};

	const handleApplyDateRange = () => {
		setAppliedFromDate(fromDate);
		setAppliedToDate(toDate);
		setCurrentPage(0);
		clearVoucherSelection();
	};

	const handleFromDateChange = (date) => {
		setFromDate(date);

		if (date && toDate && date > toDate) {
			setToDate(null);
		}
	};

	const handleToDateChange = (date) => {
		if (date && fromDate && date < fromDate) {
			setToDate(null);
			return;
		}

		setToDate(date);
	};

	const handleApplyArchiveFilters = () => {
		setAppliedArchiveFromDate(archiveFromDate);
		setAppliedArchiveToDate(archiveToDate);
		setArchiveCurrentPage(0);
	};

	const handleArchiveFromDateChange = (date) => {
		setArchiveFromDate(date);

		if (date && archiveToDate && date > archiveToDate) {
			setArchiveToDate(null);
		}
	};

	const handleArchiveToDateChange = (date) => {
		if (date && archiveFromDate && date < archiveFromDate) {
			setArchiveToDate(null);
			return;
		}

		setArchiveToDate(date);
	};

	const handleSortChange = (column) => {
		const nextSortState = getNextSortState(column, sortColumn, sortDirection);

		setCurrentPage(0);
		setSortColumn(nextSortState.sortColumn);
		setSortDirection(nextSortState.sortDirection);
	};

	const handleArchiveSortChange = (column) => {
		const nextSortState = getNextSortState(
			column,
			archiveSortColumn,
			archiveSortDirection,
		);

		setArchiveCurrentPage(0);
		setArchiveSortColumn(nextSortState.sortColumn);
		setArchiveSortDirection(nextSortState.sortDirection);
	};

	return (
		hasSettings && (
			<>
				<Header activeTab={5} />
				<div style={inlineStyles.page}>
					<div className="container">
						<div className="row">
							<div className="col-12">
								<PageTitle>
									<FormattedMessage {...messages.lblTitle} />
								</PageTitle>
							</div>
						</div>
						<div className="row">
							<div className="col-12">
								<TabBar>
									<Tabs
										value={selectedTab}
										onChange={handleTabChange}
										aria-label="Ouigo Refunds views"
										style={inlineStyles.tabs}
										TabIndicatorProps={{ style: { display: 'none' } }}
									>
										<OuiGoTab
											id="ouigo-refunds-available-tab"
											aria-controls="ouigo-refunds-available-panel"
											disableRipple
											label={<FormattedMessage {...messages.lblAvailable} />}
											style={{
												...inlineStyles.tab,
												...(selectedTab === 0 ? inlineStyles.activeTab : {}),
											}}
										/>
										<OuiGoTab
											id="ouigo-refunds-archive-tab"
											aria-controls="ouigo-refunds-archive-panel"
											disableRipple
											label={<FormattedMessage {...messages.lblArchive} />}
											style={{
												...inlineStyles.tab,
												...(selectedTab === 1 ? inlineStyles.activeTab : {}),
											}}
										/>
									</Tabs>
									{selectedTab === 0 && (
										<VouchersToolbar
											fromDate={fromDate}
											toDate={toDate}
											onFromDateChange={handleFromDateChange}
											onToDateChange={handleToDateChange}
											onApply={handleApplyDateRange}
											resultStart={resultStart}
											resultEnd={resultEnd}
											resultTotal={availableResultTotal}
										/>
									)}
									{selectedTab === 1 && (
										<VouchersToolbar
											fromDate={archiveFromDate}
											toDate={archiveToDate}
											onFromDateChange={handleArchiveFromDateChange}
											onToDateChange={handleArchiveToDateChange}
											onApply={handleApplyArchiveFilters}
											resultStart={archiveResultStart}
											resultEnd={archiveResultEnd}
											resultTotal={archiveResultTotal}
										/>
									)}
								</TabBar>
								<div
									role="tabpanel"
									hidden={selectedTab !== 0}
									id="ouigo-refunds-available-panel"
									aria-labelledby="ouigo-refunds-available-tab"
									style={inlineStyles.panel}
								>
									{selectedTab === 0 && (
										<>
											<VouchersTable
												ariaLabel="Available OuiGo refund vouchers with issue dates and values"
												hasError={hasAvailableVouchersError}
												isSelectable
												isLoading={isAvailableVouchersLoading}
												onCurrentPageSelectionChange={
													handleCurrentPageSelectionChange
												}
												vouchers={availableVouchers}
												selectedVoucherIds={selectedVoucherIds}
												sortColumn={sortColumn}
												onSortChange={handleSortChange}
												onVoucherSelectionChange={handleVoucherSelectionChange}
											/>
											<VouchersPagination
												currentPage={currentPage}
												onPageChange={setCurrentPage}
												totalPages={
													shouldShowAvailableResults
														? availablePagination.totalPages
														: 0
												}
											/>
											<SubmitVouchersAction
												isSubmitting={isSubmittingVouchers}
												onSubmit={handleSubmitVouchers}
												selectedVoucherCount={selectedVoucherIds.length}
											/>
										</>
									)}
								</div>
								<div
									role="tabpanel"
									hidden={selectedTab !== 1}
									id="ouigo-refunds-archive-panel"
									aria-labelledby="ouigo-refunds-archive-tab"
									style={inlineStyles.panel}
								>
									{selectedTab === 1 && (
										<>
											<VouchersTable
												ariaLabel="Archived OuiGo refund vouchers with issue dates, finalised dates and values"
												hasError={hasArchivedVouchersError}
												headerAction={
													<RuTertiaryButton
														aria-label="Download CSV"
														color="subt_1"
														disabled={
															!shouldShowArchiveResults ||
															archiveResultTotal === 0 ||
															isArchiveCsvDownloading
														}
														onClick={downloadArchiveCsv}
														type="button"
													>
														<FormattedMessage {...messages.lblDownloadCsv} />
														<FileDownloadOutlinedIcon
															aria-hidden="true"
															fontSize="small"
														/>
													</RuTertiaryButton>
												}
												isLoading={isArchivedVouchersLoading}
												showFinalizedDateColumn
												vouchers={archivedVouchers}
												sortColumn={archiveSortColumn}
												onSortChange={handleArchiveSortChange}
											/>
											<VouchersPagination
												currentPage={archiveCurrentPage}
												onPageChange={setArchiveCurrentPage}
												totalPages={
													shouldShowArchiveResults
														? archivePagination.totalPages
														: 0
												}
											/>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
				<VoucherSubmissionResultDialogue
					messages={submissionMessages}
					onClose={handleSubmissionResultClose}
					result={submissionResult}
				/>
				<ExtendedSnackbar
					id="ouigoArchiveCsvError"
					open={hasArchiveCsvError}
					message={intl.formatMessage(messages.lblUnableToDownloadCsv)}
					onClose={clearArchiveCsvError}
					autoHideDuration={4000}
				/>
			</>
		)
	);
};

OuigoRefunds.propTypes = {
	exportFinalizedVouchers: PropTypes.func,
	loadEligibleVouchers: PropTypes.func,
	loadFinalizedVouchers: PropTypes.func,
	loginId: PropTypes.string,
	settings: PropTypes.object,
	submitVouchers: PropTypes.func,
};

const mapStateToProps = (state) => ({
	loginId: state.getIn(['settings', 'UserLoginId']),
	settings: state.get('settings'),
});

export { OuigoRefunds as OuigoRefundsAlias };
export default connect(mapStateToProps)(OuigoRefunds);
